<template>
  <form @submit.prevent="validateBeforeSubmit">
    <div class="columns">
      <div class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading has-text-distance">
                {{ $t('compCarbonFormsTravelsForm.total-distance') }}
                <b-tooltip
                  size="is-large"
                  multilined
                  position="is-right"
                  dashed
                  type="is-black"
                >
                  ({{
                    $t('compCarbonFormsTravelsForm.understand-calculation')
                  }})
                  <template v-slot:content>
                    <span
                      v-html="
                        $t('compCarbonFormsTravelsForm.understanding-txt')
                      "
                    />
                  </template>
                </b-tooltip>
              </p>
              <div class="title is-size-4 has-text-distance">
                <p v-if="displayValues">
                  {{ distance | splitNumber }}
                  <span class="is-size-6">km</span>
                </p>
                <p v-else>
                  ...
                  <span class="is-size-6">km</span>
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
      <div class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading has-text-carbone">
                {{ $t('compCarbonFormsTravelsForm.carbon-footprint') }}
                <b-tooltip
                  size="is-large"
                  multilined
                  position="is-left"
                  dashed
                  type="is-black"
                >
                  ({{
                    $t('compCarbonFormsTravelsForm.understand-calculation')
                  }})
                  <template v-slot:content>
                    <span
                      v-html="$t('compCarbonFormsTravelsForm.emissions-txt')"
                    />
                  </template>
                </b-tooltip>
              </p>
              <div class="title is-size-4 has-text-carbone">
                <p v-if="displayValues">
                  {{ Math.round(intensity.intensity) | splitNumber }} &plusmn;
                  {{ Math.round(intensity.uncertainty) | splitNumber }}
                  <span class="is-size-6">{{ $t('kg.co2e') }}</span>
                </p>
                <p v-else>
                  ...
                  <span class="is-size-6">{{ $t('kg.co2e') }}</span>
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
    </div>
    <b-notification v-if="!isSimulation" :closable="false">
      <div class="columns">
        <div class="column">
          <b-field
            :label="$t('id') + '*'"
            :type="{ 'is-danger': errors.has('names') }"
            :message="errors.first('names')"
          >
            <b-taginput
              :disabled="!modeCreation"
              name="names"
              v-validate="'required'"
              icon="tags"
              v-model="ctravel.names"
            ></b-taginput>
          </b-field>
        </div>
        <div class="column">
          <b-field
            :label="$t('compCarbonFormsTravelsForm.field-5') + '*'"
            :type="{ 'is-danger': errors.has('amount') }"
            :message="errors.first('amount')"
          >
            <b-input
              name="amount"
              @input="updateDistance"
              v-validate="'required|numeric|min_value:1'"
              v-model.number="ctravel.amount"
              type="number"
            ></b-input>
          </b-field>
        </div>
        <div class="column">
          <b-field :label="$t('compCarbonFormsTravelsForm.field-7')">
            <b-select
              expanded
              :placeholder="$t('compCarbonFormsTravelsForm.field-7')"
              v-model="ctravel.purpose"
            >
              <option
                :value="purpose"
                v-for="(purpose, index) in purposes"
                :key="index"
              >
                {{ $t(purpose) }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="column">
          <b-field :label="$t('compCarbonFormsTravelsForm.field-6')">
            <b-select
              expanded
              :placeholder="$t('compCarbonFormsTravelsForm.field-6')"
              v-model="ctravel.status"
            >
              <option
                :value="status"
                v-for="(status, index) in allStatus"
                :key="index"
              >
                {{ $t(status) }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
    </b-notification>
    <div class="columns">
      <div class="column">
        <b-notification :closable="false">
          <b-steps :has-navigation="false" v-model="activeSection">
            <b-step-item
              v-for="(section, index) in ctravel.sections"
              :key="index"
              :step="stepDisplay(index)"
              :type="
                !modeCreation && !isSimulation && !section.isValid()
                  ? 'is-danger'
                  : 'is-primary'
              "
              class="mt-1 mb-1"
              :clickable="true"
            >
              <h1 class="title has-text-centered mb-2">
                {{ $t('compCarbonFormsTravelsForm.section') }}
                {{ stepDisplay(index) }}
              </h1>
              <br />
              <travel-section-form
                v-model="ctravel.sections[index]"
                @input="updateDistance"
                @update-departure="updateDeparture(index, ...arguments)"
                @update-destination="updateDestination(index, ...arguments)"
              />
            </b-step-item>
          </b-steps>
        </b-notification>
        <div class="columns">
          <div class="column">
            <b-button
              expanded
              icon-left="minus"
              :disabled="
                activeSection !== ctravel.sections.length - 1 ||
                activeSection === 0
              "
              @click="deleteSection"
              >{{ $t('compCarbonFormsTravelsForm.delete') }}</b-button
            >
          </div>
          <div class="column">
            <b-button
              expanded
              :disabled="activeSection !== ctravel.sections.length - 1"
              type="is-primary"
              icon-left="plus"
              @click="addSection"
            >
              {{ $t('compCarbonFormsTravelsForm.add-step') }}
            </b-button>
          </div>
        </div>
      </div>
      <div class="column">
        <l-map
          ref="map"
          :bounds="bounds"
          style="z-index: 0"
          :style="[
            !ctravel.latLngIsValid()
              ? { border: '1px solid #ff3880' }
              : { border: '1px solid #4280b0' }
          ]"
        >
          <l-tilelayer
            url="jawgUrl"
            attribution="<a href='https://jawg.io?utm_medium=map&utm_source=attribution' title='Tiles Courtesy of Jawg Maps' target='_blank' class='jawg-attrib' >&copy; <b>Jawg</b>Maps</a> | <a href='https://www.openstreetmap.org/copyright' title='OpenStreetMap is open data licensed under ODbL' target='_blank' class='osm-attrib'>&copy; OSM contributors</a>"
          ></l-tilelayer>
          <l-marker :lat-lng="firstMarker" :icon="createIcon(1)" />
          <template v-for="(section, index) in ctravel.sections">
            <l-marker
              v-if="
                section.destinationCityLat !== 0 &&
                section.destinationCityLng !== 0 &&
                section.destinationCityLat !== null &&
                section.destinationCityLng !== null
              "
              :key="index"
              :lat-lng="[
                section.destinationCityLat,
                section.destinationCityLng
              ]"
              :icon="createIcon(index + 2)"
            />
          </template>
        </l-map>
      </div>
    </div>

    <b-notification
      v-if="authStore.isAuthenticated && !isSimulation"
      :closable="false"
    >
      <tags-input v-model="ctravel.tags" />
    </b-notification>

    <hr v-if="!isSimulation" />
    <nav v-if="!isSimulation" class="level">
      <div class="level-left"></div>
      <div class="buttons level-right">
        <b-button class="button" @click="$emit('close')" icon-left="close">
          {{ $t('cancel') }}
        </b-button>
        <button type="submit" class="button is-primary">
          <span class="icon is-small"><i class="fa fa-plus"></i></span>
          <span v-if="modeCreation"> {{ $t('add') }} </span>
          <span v-else> {{ $t('update') }} </span>
        </button>
      </div>
    </nav>
  </form>
</template>

<script>
import { useAuthenticationStore } from '@/stores/authentication.js'
import L, { latLng, latLngBounds } from 'leaflet'
import iconBlue from '@/assets/images/leaflet-markers/marker-icon-blue-full.png'
import iconGrey from '@/assets/images/leaflet-markers/marker-icon-grey-full.png'
import shadow from '@/assets/images/leaflet-markers/marker-shadow.png'
import TravelSection from '@/models/carbon/TravelSection.js'
import Travel from '@/models/carbon/Travel.js'
import '@/plugins/leaflet.js'
import 'leaflet.geodesic'
import { useCoreStore } from '../../../../stores/core'

import _ from 'lodash'

L.NumberedBlueIcon = L.Icon.extend({
  options: {
    iconUrl: iconBlue,
    number: '',
    shadowUrl: shadow,
    iconSize: new L.Point(25, 41),
    iconAnchor: new L.Point(13, 41),
    popupAnchor: new L.Point(0, -33),
    className: 'leaflet-div-icon'
  },

  createIcon () {
    const div = document.createElement('div')
    const img = this._createImg(this.options.iconUrl)
    const numdiv = document.createElement('div')
    numdiv.setAttribute('class', 'marker-number')
    numdiv.innerHTML = this.options.number || ''
    div.appendChild(img)
    div.appendChild(numdiv)
    div.classList.add('leaflet-div-icon')
    this._setIconStyles(div, 'icon')
    return div
  },

  createShadow () {
    const div = document.createElement('div')
    const img = this._createImg(this.options.shadowUrl)
    div.appendChild(img)
    this._setIconStyles(div, 'icon')
    return div
  }
})

L.NumberedGreyIcon = L.Icon.extend({
  options: {
    iconUrl: iconGrey,
    number: '',
    shadowUrl: shadow,
    iconSize: new L.Point(25, 41),
    iconAnchor: new L.Point(13, 41),
    popupAnchor: new L.Point(0, -33),
    className: 'leaflet-div-icon'
  },

  createIcon () {
    const div = document.createElement('div')
    const img = this._createImg(this.options.iconUrl)
    const numdiv = document.createElement('div')
    numdiv.setAttribute('class', 'marker-number')
    numdiv.innerHTML = this.options.number || ''
    div.appendChild(img)
    div.appendChild(numdiv)
    div.classList.add('leaflet-div-icon')
    this._setIconStyles(div, 'icon')
    return div
  },

  createShadow () {
    const div = document.createElement('div')
    const img = this._createImg(this.options.shadowUrl)
    div.appendChild(img)
    this._setIconStyles(div, 'icon')
    return div
  }
})

export default {
  name: 'Form',
  props: {
    travel: {
      type: Travel,
      required: true
    },
    modeCreation: {
      type: Boolean,
      default: false
    },
    isSimulation: {
      type: Boolean,
      default: false
    }
  },
  components: {
    'l-map': () => import('vue2-leaflet').then(({ LMap }) => LMap),
    'l-tilelayer': () =>
      import('vue2-leaflet').then(({ LTileLayer }) => LTileLayer),
    'l-marker': () => import('vue2-leaflet').then(({ LMarker }) => LMarker),
    'tags-input': () => import('@/components/core/TagsInput.vue'),
    'travel-section-form': () =>
      import('@/components/carbon/forms/travels/SectionForm.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  setup () {
    const authStore = useAuthenticationStore()
    const coreStore = useCoreStore()
    return { authStore, coreStore }
  },
  data () {
    return {
      ctravel: _.cloneDeep(this.travel),
      transportations: Travel.transportations,
      intensity: null,
      distance: null,
      geodesicLines: null,
      currentGeodesicLines: null,
      activeSection: 0,
      jawgUrl: `https://tile.jawg.io/${process.env.VUE_APP_JAWG_MAP_STYLE}/{z}/{x}/{y}.png?access-token=${process.env.VUE_APP_JAWG_MAP_ACCESS_TOKEN}`
    }
  },
  computed: {
    purposes: function () {
      return this.coreStore.travelPurposeLabels
    },
    allStatus: function () {
      return this.coreStore.travelPositionLabels
    },
    displayValues: function () {
      return (
        this.ctravel.isComplete() &&
        this.distance !== null &&
        this.distance !== 0
      )
    },
    GHGIYear: function () {
      return new Date().getFullYear()
    },
    firstMarker: function () {
      if (this.ctravel.sections.length > 0) {
        return [
          this.ctravel.sections[0].departureCityLat,
          this.ctravel.sections[0].departureCityLng
        ]
      } else {
        return [0.0, 0.0]
      }
    },
    bounds: function () {
      if (this.ctravel.sections.length > 0 && this.ctravel.latLngIsValid()) {
        if (
          this.ctravel.sections[0].departureCity !== null ||
          this.ctravel.sections[0].destinationCity !== null
        ) {
          let lons = this.ctravel.sections.map((section) =>
            parseFloat(section.departureCityLng)
          )
          lons.push(
            ...this.ctravel.sections.map((section) =>
              parseFloat(section.destinationCityLng)
            )
          )
          if (lons.indexOf(0) !== -1) {
            lons.splice(lons.indexOf(0), 1)
          }
          let lats = this.ctravel.sections.map((section) =>
            parseFloat(section.departureCityLat)
          )
          lats.push(
            ...this.ctravel.sections.map((section) =>
              parseFloat(section.destinationCityLat)
            )
          )
          if (lats.indexOf(0) !== -1) {
            lats.splice(lats.indexOf(0), 1)
          }
          return latLngBounds([
            latLng(Math.min(...lats) - 0.5, Math.min(...lons) - 0.5),
            latLng(Math.max(...lats) + 0.5, Math.max(...lons) + 0.5)
          ])
        } else {
          return latLngBounds([latLng(-20, -20), latLng(20, 20)])
        }
      } else {
        return latLngBounds([latLng(-20, -20), latLng(20, 20)])
      }
    }
  },
  methods: {
    modeIcon: function () {
      return Travel.getIcon(this.ctravel.transportation)
    },
    modePack: function () {
      return Travel.getIconPack(this.ctravel.transportation)
    },
    addSection () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          let savedDestination = Object.assign(
            {},
            this.ctravel.sections[this.ctravel.sections.length - 1]
          )
          let ctravelSection = new TravelSection()
          if (!savedDestination.isRoundTrip) {
            ctravelSection.departureCity = savedDestination.destinationCity
            ctravelSection.departureCountry =
              savedDestination.destinationCountry
            ctravelSection.departureCityLat =
              savedDestination.destinationCityLat
            ctravelSection.departureCityLng =
              savedDestination.destinationCityLng
          } else {
            ctravelSection.departureCity = savedDestination.departureCity
            ctravelSection.departureCountry = savedDestination.departureCountry
            ctravelSection.departureCityLat = savedDestination.departureCityLat
            ctravelSection.departureCityLng = savedDestination.departureCityLng
          }
          this.ctravel.addSection(ctravelSection)
          this.drawGeodesicLines()
          // dirty but not working directly
          let self = this
          setTimeout(function () {
            self.activeSection = self.ctravel.sections.length - 1
          }, 5)
        }
      })
    },
    stepDisplay (index) {
      let display = index + 1
      if (this.ctravel.sections[index].destinationCity !== null) {
        let index2 = index + 2
        display += '-' + index2
      }
      return display
    },
    updateDeparture (index, section, prevCityName) {
      if (
        index > 0 &&
        this.ctravel.sections[index - 1].destinationCity === prevCityName
      ) {
        this.ctravel.sections[index - 1].destinationCity = section.departureCity
        this.ctravel.sections[index - 1].destinationCountry =
          section.departureCountry
        this.ctravel.sections[index - 1].destinationCityLat =
          section.departureCityLat
        this.ctravel.sections[index - 1].destinationCityLng =
          section.departureCityLng
        this.updateDistance()
        this.drawGeodesicLines()
      }
    },
    updateDestination (index, section, prevCityName) {
      if (
        index < this.ctravel.sections.length - 1 &&
        this.ctravel.sections[index + 1].departureCity === prevCityName
      ) {
        this.ctravel.sections[index + 1].departureCity = section.destinationCity
        this.ctravel.sections[index + 1].departureCountry =
          section.destinationCountry
        this.ctravel.sections[index + 1].departureCityLat =
          section.destinationCityLat
        this.ctravel.sections[index + 1].departureCityLng =
          section.destinationCityLng
        this.updateDistance()
        this.drawGeodesicLines()
      }
    },
    deleteSection () {
      this.ctravel.sections.splice(this.ctravel.sections.length - 1, 1)
      this.updateDistance()
      this.drawGeodesicLines()
      this.activeSection = this.ctravel.sections.length - 1
    },
    createIcon (number) {
      if (number === this.activeSection + 1) {
        return new L.NumberedBlueIcon({ number: number.toString() })
      } else if (number === this.activeSection + 2) {
        return new L.NumberedBlueIcon({ number: number.toString() })
      } else {
        return new L.NumberedGreyIcon({ number: number.toString() })
      }
    },
    drawGeodesicLines () {
      if (this.$refs.map !== undefined) {
        const map = this.$refs.map.mapObject
        let sectionsOK = this.ctravel.sections.filter((obj) => {
          return obj.departureCity !== null && obj.destinationCity !== null
        })
        if (sectionsOK.length > 0) {
          let coordsOK = []
          let currentCoords = []
          let index = 0
          for (let section of sectionsOK) {
            if (index === this.activeSection) {
              currentCoords.push([
                new L.LatLng(
                  section.departureCityLat,
                  section.departureCityLng
                ),
                new L.LatLng(
                  section.destinationCityLat,
                  section.destinationCityLng
                )
              ])
            } else {
              coordsOK.push([
                new L.LatLng(
                  section.departureCityLat,
                  section.departureCityLng
                ),
                new L.LatLng(
                  section.destinationCityLat,
                  section.destinationCityLng
                )
              ])
            }
            index += 1
          }
          if (this.geodesicLines) {
            this.geodesicLines.setLatLngs(coordsOK)
          } else {
            this.geodesicLines = new L.Geodesic(coordsOK, {
              color: 'grey'
            }).addTo(map)
          }
          if (this.currentGeodesicLines) {
            this.currentGeodesicLines.setLatLngs(currentCoords)
          } else {
            this.currentGeodesicLines = new L.Geodesic(currentCoords).addTo(map)
          }
        }
      }
    },
    validateBeforeSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.modeCreation) {
            this.$emit('add', this.ctravel)
          } else {
            this.$emit('update', {
              from: this.travel,
              to: this.ctravel
            })
          }
        }
      })
    },
    updateDistance: function () {
      this.distance = Math.round(this.ctravel.getCumulativeDistance())
      this.drawGeodesicLines()
      this.updateIntensity()
    },
    updateIntensity: function () {
      this.intensity = this.ctravel.getCarbonIntensity(this.GHGIYear)
    }
  },
  watch: {
    activeSection: function () {
      this.drawGeodesicLines()
    }
  },
  mounted () {
    let self = this
    setTimeout(function () {
      self.updateDistance()
    }, 500)
    let index = 0
    for (let section of this.ctravel.sections) {
      if (!section.isValid()) {
        break
      }
      index += 1
    }
    this.activeSection = index
    if (!this.modeCreation && !this.isSimulation) {
      this.$validator.validateAll()
    }
  }
}
</script>

<style>
.has-text-distance {
  color: #935458 !important;
}
.has-text-carbone {
  color: #8e6a9e !important;
}
.has-text-carbone2 {
  color: #208dbc !important;
}
.leaflet-div-icon {
  background: transparent;
  border: none;
}

.leaflet-marker-icon .marker-number {
  position: relative;
  top: -45px;
  color: white;
  font-weight: bold;
  font-size: 1rem;
  text-align: center;
}
a.step-link.is-clickable {
  text-decoration: none !important;
}
.heading .tooltip-content {
  text-transform: none;
}
.heading .b-tooltip.is-multiline.is-large .tooltip-content {
  width: 500px;
}
.b-tooltip.is-multiline .tooltip-content {
  text-align: left !important;
}
</style>
